import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { DownloadTokenResultDto, GetByIdsInputDto } from '../dtos/shared/models';
import type { GetStandsByNamesInputDto, GetStandsInputDto, ImportStandsInputDto, StandCreateDto, StandDto, StandExcelDownloadDto, StandGisAreaMismatchDto, StandUpdateDto } from '../dtos/stand/models';

@Injectable({
  providedIn: 'root',
})
export class StandService {
  private restService = inject(RestService);
  apiName = 'Planning';
  

  create = (input: StandCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto>({
      method: 'POST',
      url: '/api/planning/stands',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/planning/stands/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteByIds = (ids: number[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/planning/stands',
      params: { ids },
    },
    { apiName: this.apiName,...config });
  

  findByNames = (input: GetStandsByNamesInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto[]>({
      method: 'POST',
      url: '/api/planning/stands/by-names',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto>({
      method: 'GET',
      url: `/api/planning/stands/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getDownloadToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DownloadTokenResultDto>({
      method: 'GET',
      url: '/api/planning/stands/download-token',
    },
    { apiName: this.apiName,...config });
  

  getGisAreaMismatches = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandGisAreaMismatchDto[]>({
      method: 'GET',
      url: '/api/planning/stands/gis-area-mismatches',
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetStandsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<StandDto>>({
      method: 'GET',
      url: '/api/planning/stands',
      params: { name: input.name, propertyId: input.propertyId, zoneId: input.zoneId, subregionId: input.subregionId, regionId: input.regionId, propertyName: input.propertyName, standArea: input.standArea, standAreaFrom: input.standAreaFrom, standAreaTo: input.standAreaTo, gisArea: input.gisArea, gisAreaFrom: input.gisAreaFrom, gisAreaTo: input.gisAreaTo, forestrySituationId: input.forestrySituationId, forestrySituationName: input.forestrySituationName, isStrategic: input.isStrategic, hasNearbyPopulation: input.hasNearbyPopulation, standTypeId: input.standTypeId, standTypeName: input.standTypeName, locationId: input.locationId, locationName: input.locationName, locationIsActive: input.locationIsActive, currentCycle: input.currentCycle, currentCycleFrom: input.currentCycleFrom, currentCycleTo: input.currentCycleTo, currentRotation: input.currentRotation, currentRotationFrom: input.currentRotationFrom, currentRotationTo: input.currentRotationTo, isPefcCertified: input.isPefcCertified, isFscCertified: input.isFscCertified, deactivationReasonId: input.deactivationReasonId, deactivationDate: input.deactivationDate, deactivationDateFrom: input.deactivationDateFrom, deactivationDateTo: input.deactivationDateTo, previousSoilUseId: input.previousSoilUseId, previousSoilUse: input.previousSoilUse, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListAsExcelFile = (input: StandExcelDownloadDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/planning/stands/as-excel-file',
      params: { downloadToken: input.downloadToken, fields: input.fields, name: input.name, propertyId: input.propertyId, zoneId: input.zoneId, subregionId: input.subregionId, regionId: input.regionId, propertyName: input.propertyName, standArea: input.standArea, standAreaFrom: input.standAreaFrom, standAreaTo: input.standAreaTo, gisArea: input.gisArea, gisAreaFrom: input.gisAreaFrom, gisAreaTo: input.gisAreaTo, forestrySituationId: input.forestrySituationId, forestrySituationName: input.forestrySituationName, isStrategic: input.isStrategic, hasNearbyPopulation: input.hasNearbyPopulation, standTypeId: input.standTypeId, standTypeName: input.standTypeName, locationId: input.locationId, locationName: input.locationName, locationIsActive: input.locationIsActive, currentCycle: input.currentCycle, currentCycleFrom: input.currentCycleFrom, currentCycleTo: input.currentCycleTo, currentRotation: input.currentRotation, currentRotationFrom: input.currentRotationFrom, currentRotationTo: input.currentRotationTo, isPefcCertified: input.isPefcCertified, isFscCertified: input.isFscCertified, deactivationReasonId: input.deactivationReasonId, deactivationDate: input.deactivationDate, deactivationDateFrom: input.deactivationDateFrom, deactivationDateTo: input.deactivationDateTo, previousSoilUseId: input.previousSoilUseId, previousSoilUse: input.previousSoilUse, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListByIds = (input: GetByIdsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto[]>({
      method: 'GET',
      url: '/api/planning/stands/by-ids',
      params: { ids: input.ids },
    },
    { apiName: this.apiName,...config });
  

  import = (input: ImportStandsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto[]>({
      method: 'POST',
      url: '/api/planning/stands/import',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: StandUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandDto>({
      method: 'PUT',
      url: `/api/planning/stands/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateGisAreas = (input: StandGisAreaMismatchDto[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'PUT',
      url: '/api/planning/stands/gis-areas',
      body: input,
    },
    { apiName: this.apiName,...config });
}